import { getAddress, type Hex } from "viem";
import type { BootstrapResponse } from "../../../api/schemas";
import type { XMintAuthorization } from "../api/social-tasks-schemas";
import { responseSecondsRemaining } from "../../../api/response-time";
import { decodeTaskMint } from "../contracts/task-mint";
import type { WalletSessionController } from "../../../wallet/wallet-session";
import type { TransactionTracker } from "../../../wallet/transaction-tracker";
import type { ActionContext } from "../../../state/action-context";
import { taskMintSubmissionPermission } from "../../../state/transaction-capability";
import { isElysium } from "../../../config/elysium";

export async function submitTaskMint(result: XMintAuthorization, guard: () => void, options: {
  bootstrap: BootstrapResponse | null; sourceToken: string | null; wallet: WalletSessionController;
  tracker: TransactionTracker; environment: string;
  beginOperation: ActionContext["beginOperation"]; endOperation: ActionContext["endOperation"];
}) {
  guard();
  const { bootstrap, sourceToken, wallet, tracker } = options;
  const permission = taskMintSubmissionPermission(bootstrap);
  if (permission.state !== "ALLOWED" || !bootstrap || !sourceToken) throw new Error(permission.reasons.join(", ") || "Task source unavailable");
  const account = wallet.getSnapshot().account;
  if (!account || !wallet.getSnapshot().authenticated ||
    !bootstrap.tokens.some(token => token.tracking && token.tokenAddress.toLowerCase() === sourceToken.toLowerCase())) {
    throw new Error("Task mint requires the authenticated wallet and registered source token");
  }
  if (wallet.getSnapshot().chainId !== result.chainId) throw new Error(`Switch to chain ${result.chainId}, then prepare a new task mint review.`);
  const decoded = decodeTaskMint(result, { wallet: account, sourceToken,
    chainId: bootstrap.contracts.mintChainId!, collection: bootstrap.contracts.collectionAddress! });
  const provider = wallet.getProvider();
  if (!provider) throw new Error("Connect a wallet first");
  const operation = options.beginOperation();
  const assertCurrent = () => {
    operation.assertCurrent(); guard();
    if (tracker.socialTaskClaimed({ account, chainId: result.chainId, collection: result.contractAddress, environment: options.environment })) {
      throw new Error("This wallet has already claimed its social task NFT.");
    }
    if (tracker.getSnapshot().storageWarning || tracker.getSnapshot().socialClaimStorageWarning) {
      throw new Error("Task recovery storage is unavailable. Check wallet activity before sending.");
    }
    if (responseSecondsRemaining(result, result.deadline) <= 0) throw new Error("Authorization expired. Prepare and review again.");
  };
  try {
    assertCurrent();
    const tracked = await tracker.execute({ kind: "MINT", account, chainId: result.chainId, sourceToken,
      collection: result.contractAddress, environment: options.environment, tokenId: null, mintIdentity: decoded.identity,
    }, provider, async () => {
      assertCurrent();
      return { account: getAddress(account), chainId: result.chainId, to: getAddress(result.call.to),
        data: result.call.data as Hex, valueWei: 0n, mintSigner: bootstrap.contracts.authorizationSignerAddress!,
        ...(isElysium(bootstrap) ? { mintBootstrap: () => bootstrap } : {}),
        taskMint: { ...decoded.identity, sourceToken: getAddress(sourceToken) } };
    }, { assertCurrent, signal: operation.signal });
    if (!tracked.hash && ["FAILED", "CANCELLED", "REJECTED", "UNCONFIRMED"].includes(tracked.phase)) throw new Error(tracked.message);
    return tracked;
  } finally { options.endOperation(operation); }
}
